import ModalWrapper from '@Components/Modals/ModalWrapper/ModalWrapper';
import LocationServices from '@Services/Location.services.ts';
import { Location } from '@Types/Asset.types';
import { LocationFormFields } from '@Types/Form.types';
import { formatRequired } from '@Utils/form.utils';
import { handleRequestError } from '@Utils/request.utils';
import { VALIDATION_RULES } from '@Utils/rule.utils.ts';
import { useApolloClient } from '@apollo/client';
import { Dropdown, ErrorDisplay, FreeText, Toast } from '@kg-internal/pv3-uikit';
import cx from 'classnames';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { GetOfficesDocument, GetOfficesQuery } from '~/__generated__/graph';
import FormError from '~/Components/FormError/FormError';
import { formatCountryDropdown } from '~/Utils/format.utils';

type Props = {
  show: boolean;
  onClose: () => void;
  onSuccess: () => void;
  location?: Location;
};

const { LOCATION_RULES } = VALIDATION_RULES;
const LocationModal = ({ show, onClose, onSuccess, location }: Props) => {
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [countries, setCountries] = useState<string[]>([]);
  const client = useApolloClient();
  const {
    handleSubmit,
    formState: { errors },
    reset,
    register,
    setValue,
    control,
  } = useForm<LocationFormFields>();

  useEffect(() => {
    reset();
    if (location) {
      setValue('name', location.name);
      setValue('location', location.location);
      setValue('country', location.country);
      setValue('details', location.description);
    }
  }, [location, show]);

  useEffect(() => {
    const getOffice = async () => {
      const { data: kgOffices, error } = await client.query<GetOfficesQuery>({
        query: GetOfficesDocument,
      });

      if (error) {
        Toast.warning('Error loading countries');
        return;
      }

      if (!kgOffices || !kgOffices.officeListV2) {
        setCountries([]);
      } else {
        const countrySet = kgOffices.officeListV2.reduce((prev: Set<string>, office) => {
          if (office.Country) {
            prev.add(office.Country);
          }
          return prev;
        }, new Set<string>());
        setCountries(Array.from(countrySet));
      }
    };

    getOffice();
  }, []);

  const onSubmitSuccess = () => {
    Toast.success(location ? 'Location updated successfully.' : 'Location created successfully.');
    onSuccess();
    reset();
    onClose();
  };

  const handleCreateLocation = (data: LocationFormFields) => {
    LocationServices.createLocation(data)
      .then(() => onSubmitSuccess())
      .catch((error) => handleRequestError(error, setErrorMessage, true));
  };
  const handleUpdateLocation = (data: LocationFormFields) => {
    LocationServices.updateLocation(location?.id, data)
      .then(() => onSubmitSuccess())
      .catch((error) => handleRequestError(error, setErrorMessage, true));
  };

  const title = location ? 'Edit Location' : 'Add Location';
  const handleLocationSumbit = location ? handleUpdateLocation : handleCreateLocation;

  return (
    <div className='modal--fit-content modal--pt-25vh modal--w-40vw'>
      <ModalWrapper
        title={title}
        primaryButtonText='Confirm'
        show={show}
        handleClose={onClose}
        primaryAction={() => handleSubmit(handleLocationSumbit)()}>
        {errorMessage ? (
          <ErrorDisplay inline details={errorMessage} />
        ) : (
          <form onSubmit={() => handleSubmit(handleLocationSumbit)()}>
            <div className='grid'>
              <div className='col-12'>
                <FreeText
                  label={formatRequired('Name')}
                  fullWidth
                  error={!!errors.name}
                  errorMessage={errors.name?.message}
                  {...register('name', LOCATION_RULES.NAME)}
                />
                <FreeText
                  label={formatRequired('Location')}
                  fullWidth
                  error={!!errors.location}
                  errorMessage={errors.location?.message}
                  {...register('location', LOCATION_RULES.LOCATION)}
                />
                <span
                  className={cx({
                    'form-error': errors.country?.message,
                  })}>
                  <Dropdown.RHF
                    options={formatCountryDropdown(countries)}
                    label={formatRequired('Country')}
                    name='country'
                    control={control}
                    rules={LOCATION_RULES.COUNTRY}
                    id='country'
                    fullWidth
                  />
                  <FormError error={errors.country?.message} />
                </span>
                <FreeText
                  label='Details'
                  fullWidth
                  error={!!errors.details}
                  errorMessage={errors.details?.message}
                  {...register('details', LOCATION_RULES.DETAILS)}
                />
              </div>
            </div>
          </form>
        )}
      </ModalWrapper>
    </div>
  );
};

export default LocationModal;
